import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Users, Crown } from 'lucide-react';
import { baseUrl } from '../config';

/**
 * Community Switcher - Top Nav Component
 * Shows user's communities as avatars with admin badges
 */
const CommunitySwitcher = ({ onCommunitySwitch, currentCommunityId }) => {
    const [userCommunities, setUserCommunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hoveredCommunity, setHoveredCommunity] = useState(null);

    useEffect(() => {
        loadUserCommunities();
    }, []);

    const loadUserCommunities = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("access_token"); // ✅ CORRECT
            
            const response = await fetch(`${baseUrl}/community/my-communities`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                setUserCommunities(data);
            } else {
                setUserCommunities([]);
            }
        } catch (error) {
            setUserCommunities([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCommunityClick = (community) => {
        onCommunitySwitch(community);
    };

    const getAvatarColor = (name) => {
        const colors = [
            'bg-teal-600',
            'bg-blue-600',
            'bg-purple-600',
            'bg-pink-600',
            'bg-indigo-600',
            'bg-cyan-600'
        ];
        const index = name.charCodeAt(0) % colors.length;
        return colors[index];
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="w-10 h-10 bg-gray-200 rounded-full animate-pulse"></div>
                ))}
            </div>
        );
    }

    if (userCommunities.length === 0) {
        return null;
    }

    return (
        <div className="flex items-center gap-2">
            {/* Label */}
            <span className="hidden lg:block text-sm font-medium text-gray-600 mr-1">
                Your Communities:
            </span>

            {/* Community Avatars */}
            <div className="flex items-center gap-2">
                {userCommunities.slice(0, 5).map((community) => {
                    const isActive = currentCommunityId === community.community_id;
                    const isAdmin = community.is_admin || community.role === 'admin' || community.role === 'creator';
                    const avatarColor = getAvatarColor(community.name);
                    const firstLetter = community.name.charAt(0).toUpperCase();

                    return (
                        <div 
                            key={community.community_id}
                            className="relative"
                            onMouseEnter={() => setHoveredCommunity(community.community_id)}
                            onMouseLeave={() => setHoveredCommunity(null)}
                        >
                            <button
                                onClick={() => handleCommunityClick(community)}
                                className={`relative w-10 h-10 rounded-full flex items-center justify-center font-bold text-white transition-all duration-200 cursor-pointer
                                    ${avatarColor}
                                    ${isActive ? 'ring-4 ring-teal-300 ring-offset-2 scale-110' : 'hover:scale-105 hover:ring-2 hover:ring-gray-300'}
                                `}
                            >
                                {community.logo_url ? (
                                    <img 
                                        src={community.logo_url}
                                        alt={community.name}
                                        className="w-full h-full object-cover rounded-full"
                                    />
                                ) : (
                                    <span className="text-base">{firstLetter}</span>
                                )}

                                {/* Admin Badge */}
                                {isAdmin && (
                                    <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-yellow-400 border-2 border-white rounded-full flex items-center justify-center shadow-md">
                                        <Crown className="h-2.5 w-2.5 text-yellow-900" />
                                    </div>
                                )}

                                {/* Active Indicator */}
                                {isActive && (
                                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></div>
                                )}
                            </button>

                            {/* Tooltip */}
                            {hoveredCommunity === community.community_id && (
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 z-50 animate-fadeIn">
                                    <div className="bg-gray-900 text-white text-xs py-2 px-3 rounded-lg shadow-xl whitespace-nowrap max-w-xs">
                                        <div className="font-semibold">{community.name}</div>
                                        {isAdmin && (
                                            <div className="text-yellow-400 text-[10px] mt-0.5 flex items-center gap-1">
                                                <Crown className="h-2.5 w-2.5" />
                                                Admin
                                            </div>
                                        )}
                                        {/* Arrow */}
                                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900"></div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* More indicator */}
                {userCommunities.length > 5 && (
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200 text-gray-700 text-xs font-semibold">
                        +{userCommunities.length - 5}
                    </div>
                )}
            </div>

            {/* World View Button */}
            <button
                onClick={() => onCommunitySwitch(null)}
                className={`ml-2 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200
                    ${!currentCommunityId ? 'bg-teal-600 text-white ring-4 ring-teal-300 ring-offset-2' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}
                `}
                title="View all communities"
            >
                <Users className="h-5 w-5" />
            </button>

            <style jsx>{`
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translate(-50%, -4px);
                    }
                    to {
                        opacity: 1;
                        transform: translate(-50%, 0);
                    }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.2s ease-out;
                }
            `}</style>
        </div>
    );
};

CommunitySwitcher.propTypes = {
    onCommunitySwitch: PropTypes.func.isRequired,
    currentCommunityId: PropTypes.number
};

export default CommunitySwitcher;
